// Advanced psychic potential comparison and compatibility logic for neural networks.

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { getFractalDimension, getWavePatternSignature, calculateNetworkResonance, analyzeNetworkEvolution } from './fractalUtils';

export interface PsychicPotentialResult {
	score: number;
	compatibility: number;
	resonance: number;
	harmonicAlignment: number;
	lifeForceSync: number;
	evolutionaryPhase: string;
	details: string;
	recommendations: string[];
}

export interface PsychicProfile {
	energySignature: number[];
	waveSignature: number[];
	fractalDimension: number;
	resonancePattern: number[];
	lifeForceLevel: number;
	evolutionaryPhase: 'birth' | 'growth' | 'maturity' | 'transformation';
	heartRhythm: number;
	networkComplexity: number;
}

// Generate comprehensive psychic profile for a network
export function generatePsychicProfile(network: NeuralNetworkSimulator): PsychicProfile {
	const fractalDimension = getFractalDimension(network);
	const waveSignature = getWavePatternSignature(network);
	const resonancePattern = calculateNetworkResonance(network);
	const evolution = analyzeNetworkEvolution(network);
	
	// Calculate energy signature
	const energySignature = network.nodes.map(node => node.energy || 0);
	
	// Calculate life force level from crystal heart
	let lifeForceLevel = 0.5;
	if (network.crystalHeartId !== null) {
		const heart = network.nodes[network.crystalHeartId];
		if (heart) {
			lifeForceLevel = (heart.energy || 0) / 10;
		}
	}
	
	// Calculate heart rhythm
	const heartRhythm = network.heartLifeForce || 1.0;
	
	// Calculate network complexity
	const networkComplexity = network.nodes.length * network.edges.length / Math.max(1, network.branches.length);
	
	return {
		energySignature: energySignature.slice(0, 20), // Limit for comparison
		waveSignature,
		fractalDimension,
		resonancePattern: resonancePattern.slice(0, 20), // Limit for comparison
		lifeForceLevel,
		evolutionaryPhase: evolution.evolutionPhase,
		heartRhythm,
		networkComplexity
	};
}

// Advanced psychic potential comparison
export function comparePsychicPotential(netA: NeuralNetworkSimulator, netB: NeuralNetworkSimulator): PsychicPotentialResult {
	const profileA = generatePsychicProfile(netA);
	const profileB = generatePsychicProfile(netB);
	
	// Energy compatibility
	const energyCompatibility = calculateVectorSimilarity(profileA.energySignature, profileB.energySignature);
	
	// Wave pattern harmony
	const waveHarmony = calculateVectorSimilarity(profileA.waveSignature, profileB.waveSignature);
	
	// Fractal dimension compatibility
	const fractalCompatibility = 1 - Math.abs(profileA.fractalDimension - profileB.fractalDimension) / Math.max(profileA.fractalDimension, profileB.fractalDimension);
	
	// Resonance alignment
	const resonanceAlignment = calculateVectorSimilarity(profileA.resonancePattern, profileB.resonancePattern);
	
	// Life force synchronization
	const lifeForceSync = 1 - Math.abs(profileA.lifeForceLevel - profileB.lifeForceLevel);
	
	// Heart rhythm harmony
	const heartRhythmHarmony = 1 - Math.abs(profileA.heartRhythm - profileB.heartRhythm) / Math.max(profileA.heartRhythm, profileB.heartRhythm);
	
	// Evolutionary phase compatibility
	const phaseCompatibility = profileA.evolutionaryPhase === profileB.evolutionaryPhase ? 1.0 : 0.5;
	
	// Overall compatibility score
	const compatibility = (energyCompatibility + waveHarmony + fractalCompatibility + resonanceAlignment + lifeForceSync + heartRhythmHarmony + phaseCompatibility) / 7;
	
	// Psychic resonance score
	const resonance = (resonanceAlignment + waveHarmony + heartRhythmHarmony) / 3;
	
	// Harmonic alignment
	const harmonicAlignment = (waveHarmony + fractalCompatibility + energyCompatibility) / 3;
	
	// Overall psychic potential score
	const score = (compatibility + resonance + harmonicAlignment + lifeForceSync) / 4;
	
	// Generate recommendations
	const recommendations = generateRecommendations(profileA, profileB, compatibility, resonance);
	
	// Determine evolutionary phase
	const evolutionaryPhase = `${profileA.evolutionaryPhase} ↔ ${profileB.evolutionaryPhase}`;
	
	const details = `Energy: ${energyCompatibility.toFixed(3)}, Waves: ${waveHarmony.toFixed(3)}, Fractal: ${fractalCompatibility.toFixed(3)}, Resonance: ${resonanceAlignment.toFixed(3)}, LifeForce: ${lifeForceSync.toFixed(3)}, Heart: ${heartRhythmHarmony.toFixed(3)}`;
	
	return {
		score,
		compatibility,
		resonance,
		harmonicAlignment,
		lifeForceSync,
		evolutionaryPhase,
		details,
		recommendations
	};
}

// Calculate similarity between two vectors
function calculateVectorSimilarity(vecA: number[], vecB: number[]): number {
	if (vecA.length === 0 || vecB.length === 0) return 0;
	
	const minLength = Math.min(vecA.length, vecB.length);
	let dotProduct = 0;
	let normA = 0;
	let normB = 0;
	
	for (let i = 0; i < minLength; i++) {
		dotProduct += vecA[i] * vecB[i];
		normA += vecA[i] * vecA[i];
		normB += vecB[i] * vecB[i];
	}
	
	const magnitudeA = Math.sqrt(normA);
	const magnitudeB = Math.sqrt(normB);
	
	if (magnitudeA === 0 || magnitudeB === 0) return 0;
	
	return dotProduct / (magnitudeA * magnitudeB);
}

// Generate compatibility recommendations
function generateRecommendations(
	profileA: PsychicProfile, 
	profileB: PsychicProfile, 
	compatibility: number, 
	resonance: number
): string[] {
	const recommendations: string[] = [];
	
	if (compatibility > 0.8) {
		recommendations.push("🌟 Exceptional compatibility - networks are in perfect harmony");
		recommendations.push("💫 Strong resonance potential for collaborative growth");
	} else if (compatibility > 0.6) {
		recommendations.push("✨ Good compatibility - networks can work together effectively");
		recommendations.push("🔄 Consider synchronized evolution cycles");
	} else if (compatibility > 0.4) {
		recommendations.push("⚡ Moderate compatibility - some adaptation needed");
		recommendations.push("🎵 Try harmonizing wave frequencies");
	} else {
		recommendations.push("🌊 Low compatibility - significant differences detected");
		recommendations.push("🔄 Consider gradual exposure for adaptation");
	}
	
	if (resonance > 0.7) {
		recommendations.push("🎶 High psychic resonance - networks vibrate in sync");
	} else if (resonance < 0.3) {
		recommendations.push("🔊 Low resonance - consider frequency adjustment");
	}
	
	if (profileA.evolutionaryPhase !== profileB.evolutionaryPhase) {
		recommendations.push(`📈 Different evolutionary phases: ${profileA.evolutionaryPhase} vs ${profileB.evolutionaryPhase}`);
	}
	
	if (Math.abs(profileA.lifeForceLevel - profileB.lifeForceLevel) > 0.3) {
		recommendations.push("💓 Life force levels differ significantly - consider energy balancing");
	}
	
	return recommendations;
}

// Analyze multiple network compatibility matrix
export function analyzeCompatibilityMatrix(networks: NeuralNetworkSimulator[]): {
	matrix: number[][];
	mostCompatible: { i: number; j: number; score: number }[];
	leastCompatible: { i: number; j: number; score: number }[];
} {
	const matrix: number[][] = [];
	const comparisons: { i: number; j: number; score: number }[] = [];
	
	// Calculate compatibility matrix
	for (let i = 0; i < networks.length; i++) {
		matrix[i] = [];
		for (let j = 0; j < networks.length; j++) {
			if (i === j) {
				matrix[i][j] = 1.0; // Perfect self-compatibility
			} else {
				const result = comparePsychicPotential(networks[i], networks[j]);
				matrix[i][j] = result.compatibility;
				comparisons.push({ i, j, score: result.compatibility });
			}
		}
	}
	
	// Find most and least compatible pairs
	const sortedComparisons = comparisons.sort((a, b) => b.score - a.score);
	const mostCompatible = sortedComparisons.slice(0, 3);
	const leastCompatible = sortedComparisons.slice(-3).reverse();
	
	return {
		matrix,
		mostCompatible,
		leastCompatible
	};
}
